import { useRef, useState, type DragEvent } from "react";
import { Upload, File as FileIcon, X } from "lucide-react";
import clsx from "clsx";
import { ProgressBar } from "./ProgressBar";

export type FileUploadCompactProps = {
  label?: string;
  hint?: string;
  accept?: string;
  multiple?: boolean;
  value?: File[];
  onChange?: (files: File[]) => void;
  maxSizeMB?: number;
  disabled?: boolean;
  error?: string;
  className?: string;
  /** Row 21.20 — per-file upload progress, keyed by the file's index in `value`/the
   *  internal list. 0-100 for a determinate bar, `null` for indeterminate, omit the
   *  index entirely for "not currently uploading." Actual upload transport (XHR/fetch
   *  progress events) is the consumer's responsibility — this component is presentational. */
  progress?: Record<number, number | null>;
};

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function FileUploadCompact({
  label,
  hint,
  accept,
  multiple = false,
  value: controlled,
  onChange,
  maxSizeMB,
  disabled,
  error,
  className,
  progress,
}: FileUploadCompactProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internal, setInternal] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const files = controlled ?? internal;

  const setFiles = (next: File[]) => {
    if (controlled === undefined) setInternal(next);
    onChange?.(next);
  };

  const handleSelect = (incoming: FileList | null) => {
    if (!incoming) return;
    let next = Array.from(incoming);
    if (maxSizeMB) {
      next = next.filter((f) => f.size <= maxSizeMB * 1024 * 1024);
    }
    setFiles(multiple ? [...files, ...next] : next.slice(0, 1));
  };

  const removeAt = (i: number) => setFiles(files.filter((_, idx) => idx !== i));

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleSelect(e.dataTransfer.files);
  };

  return (
    <div className={clsx("flex flex-col gap-1.5", className)}>
      {label && <span className="text-sm font-medium text-[var(--color-text-primary)]">{label}</span>}
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={clsx(
          "flex items-center gap-3 px-3 py-2 rounded-[var(--radius-md)] border border-dashed cursor-pointer transition-colors",
          dragOver
            ? "border-[var(--color-accent)] bg-[var(--color-accent-subtle)]"
            : "border-[var(--color-border-default)] bg-[var(--color-surface-subtle)]",
          "hover:border-[var(--color-accent)]",
          disabled && "opacity-50 cursor-not-allowed",
          error && "border-[var(--color-danger-500)]",
        )}
      >
        <Upload size={18} className="text-[var(--color-text-secondary)]" />
        <div className="flex-1 text-sm">
          <span className="text-[var(--color-text-primary)]">Click or drop files</span>
          {hint && <span className="block text-xs text-[var(--color-text-secondary)]">{hint}</span>}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => {
            handleSelect(e.currentTarget.files);
            // Let selecting the same file again emit a new change event after
            // it has been removed from the controlled or internal file list.
            e.currentTarget.value = "";
          }}
          className="hidden"
        />
      </div>
      {files.length > 0 && (
        <ul className="flex flex-col gap-1 mt-1">
          {files.map((f, i) => {
            const p = progress?.[i];
            const uploading = i in (progress ?? {});
            return (
              <li
                key={i}
                className="flex flex-col gap-1 px-2 py-1 rounded-[var(--radius-sm)] bg-[var(--color-surface-subtle)] text-xs"
              >
                <div className="flex items-center gap-2">
                  <FileIcon size={14} className="text-[var(--color-text-secondary)]" />
                  <span className="flex-1 truncate text-[var(--color-text-primary)]">{f.name}</span>
                  <span className="text-[var(--color-text-secondary)]">{formatSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeAt(i);
                    }}
                    // `progress` is keyed by ARRAY INDEX — removing a file mid-upload shifts
                    // every later index, silently reassigning progress bars to the wrong file
                    // (found by post-build review). Blocking remove while THIS index is
                    // uploading avoids the reachable case; a consumer wanting mid-upload
                    // cancellation should key its own progress state by file identity instead.
                    disabled={disabled || uploading}
                    aria-label={`Remove ${f.name}`}
                    className="text-[var(--color-text-secondary)] hover:text-[var(--color-danger-500)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-[var(--color-text-secondary)]"
                  >
                    <X size={14} />
                  </button>
                </div>
                {uploading && <ProgressBar value={p ?? null} size="sm" showValue={p != null} />}
              </li>
            );
          })}
        </ul>
      )}
      {error && <span className="text-xs text-[var(--color-danger-500)]">{error}</span>}
    </div>
  );
}
